import { useCallback, useMemo, useRef, useState } from 'react';
import { MULTIPLE_SELECTION_TYPES, SelectionType } from 'UI/constants/defaults';
import { CandidateEntity, ENTITY_TYPE_ID_MAP, EntityType } from 'UI/constants/entityTypes';

const getCandidateId = resume => resume?.candidate?.id;
const mapCandidateIdToSelectedItem = id => ({
  id,
  type: CandidateEntity.singular,
  type_id: ENTITY_TYPE_ID_MAP[EntityType.Candidate]
});

export const useResumeSelection = ({ data, totalResults }) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(null);

  const selectedIdsRef = useRef(selectedIds);
  const excludedIdsRef = useRef(excludedIds);
  const selectionModeRef = useRef(selectionMode);
  const dataRef = useRef(data);

  selectedIdsRef.current = selectedIds;
  excludedIdsRef.current = excludedIds;
  selectionModeRef.current = selectionMode;
  dataRef.current = data;

  const pageIds = useMemo(() => data.map(getCandidateId).filter(Boolean), [data]);
  const pageIdsRef = useRef(pageIds);
  pageIdsRef.current = pageIds;

  const isSelected = useCallback(candidateId => {
    if (!candidateId) return false;

    if (selectionModeRef.current === MULTIPLE_SELECTION_TYPES.all) {
      return !excludedIdsRef.current.has(candidateId);
    }

    return selectedIdsRef.current.has(candidateId);
  }, []);

  const toggleSelection = useCallback(candidateId => {
    if (!candidateId) return;

    if (selectionModeRef.current === MULTIPLE_SELECTION_TYPES.all) {
      setExcludedIds(prev => {
        const next = new Set(prev);
        if (next.has(candidateId)) {
          next.delete(candidateId);
        } else {
          next.add(candidateId);
        }
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(candidateId)) {
          next.delete(candidateId);
        } else {
          next.add(candidateId);
        }
        return next;
      });
    }
  }, []);

  const handleSelectPage = useCallback(() => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      pageIdsRef.current.forEach(id => {
        next.delete(id);
      });
      return next;
    });

    setSelectedIds(prev => {
      const next = new Set(prev);
      dataRef.current.forEach(resume => {
        const id = getCandidateId(resume);
        if (id) {
          next.add(id);
        }
      });
      return next;
    });

    setSelectionMode(null);
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set());
    setExcludedIds(new Set());

    setSelectionMode(MULTIPLE_SELECTION_TYPES.all);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setExcludedIds(new Set());

    setSelectionMode(null);
  }, []);

  const totalSelected = useMemo(() => {
    if (selectionMode === MULTIPLE_SELECTION_TYPES.all) {
      return totalResults - excludedIds.size;
    }

    return selectedIds.size;
  }, [selectionMode, totalResults, selectedIds, excludedIds]);

  const areSomeSelected = totalSelected > 0;

  const selectedIdsOnPage = useMemo(() => {
    return pageIds.filter(id => {
      if (selectionMode === MULTIPLE_SELECTION_TYPES.all) {
        return !excludedIds.has(id);
      }
      return selectedIds.has(id);
    });
  }, [pageIds, selectionMode, selectedIds, excludedIds]);

  const canMapAllSelectionToLoadedCandidates =
    selectionMode === MULTIPLE_SELECTION_TYPES.all && pageIds.length === totalResults;

  const totalResultsRef = useRef(totalResults);
  totalResultsRef.current = totalResults;

  const getSelectedIds = useCallback(() => {
    const isExcludeMode = selectionModeRef.current === MULTIPLE_SELECTION_TYPES.all;

    return {
      type: isExcludeMode ? SelectionType.Exclude : SelectionType.Include,
      ids: isExcludeMode ? Array.from(excludedIdsRef.current) : Array.from(selectedIdsRef.current),
      count: totalResultsRef.current
    };
  }, []);

  const selectedItems = useMemo(() => {
    const isExcludeMode = selectionMode === MULTIPLE_SELECTION_TYPES.all;
    const selectedIdsForSearchProject = canMapAllSelectionToLoadedCandidates
      ? selectedIdsOnPage
      : Array.from(isExcludeMode ? excludedIds : selectedIds);

    return {
      type:
        isExcludeMode && !canMapAllSelectionToLoadedCandidates
          ? SelectionType.Exclude
          : SelectionType.Include,
      data: selectedIdsForSearchProject.map(mapCandidateIdToSelectedItem),
      count: totalResults
    };
  }, [
    canMapAllSelectionToLoadedCandidates,
    excludedIds,
    selectedIds,
    selectedIdsOnPage,
    selectionMode,
    totalResults
  ]);

  const canUseSearchProjectActions = totalSelected > 0;

  return {
    isSelected,
    toggleSelection,
    handleSelectPage,
    handleSelectAll,
    handleClearSelection,
    totalSelected,
    areSomeSelected,
    selectedIdsOnPage,
    getSelectedIds,
    selectedItems,
    canUseSearchProjectActions
  };
};
